// ============================================================================
// ACTIVE SESSIONS PAGE - /admin/sessions
// System admin view of all active sessions grouped by project
// ============================================================================

import React, { useState } from 'react';
import { Activity, ShieldAlert, X, RefreshCw, Power, Clock, AlertTriangle } from 'lucide-react';
import { useActiveSessions, useForceEndSession, ActiveSessionRow, BreakGlassContext } from '../../hooks/useActiveSessions';
import { BreakGlassModal } from '../../components/common/BreakGlassModal';

const STALE_DAYS = 5;

const formatRelative = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

const formatPhase = (phase: string): string =>
  phase ? phase.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';

interface EndConfirmDialogProps {
  session: ActiveSessionRow;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  error: Error | null;
}

const EndConfirmDialog: React.FC<EndConfirmDialogProps> = ({ session, onCancel, onConfirm, loading, error }) => {
  const alreadyDone = error?.message?.toLowerCase().includes('already');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-base font-bold text-navy">End Session</h2>
            <p className="text-xs text-slate-500">This will run the full session analysis</p>
          </div>
        </div>
        <p className="text-sm text-slate-700 mb-1">
          {session.nameRedacted
            ? <>End session <span className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">{session.sessionId}</span>?</>
            : <>End session <strong>{session.customerName}</strong>?</>}
        </p>
        <p className="text-xs text-slate-500 mb-4">
          This is irreversible. The session will be analysed and marked as completed.
        </p>
        {error && (
          <div className="mb-4 px-3 py-2.5 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            {alreadyDone
              ? 'This session is already completed. The list will refresh automatically.'
              : error.message || 'Failed to end session. Try again.'}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="flex items-center justify-center gap-2 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
          >
            {alreadyDone ? 'Dismiss' : 'Cancel'}
          </button>
          {!alreadyDone && (
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex items-center justify-center gap-2 rounded-xl h-9 px-4 bg-red-600 hover:bg-red-700 transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
            >
              {loading ? <RefreshCw size={14} className="animate-spin" /> : <Power size={14} />}
              End Session
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ActiveSessions: React.FC = () => {
  const [breakGlassCtx, setBreakGlassCtx] = useState<BreakGlassContext | null>(null);
  const [breakGlassModalOpen, setBreakGlassModalOpen] = useState(false);
  const [confirmSession, setConfirmSession] = useState<ActiveSessionRow | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useActiveSessions(breakGlassCtx);
  const forceEnd = useForceEndSession();

  const handleForceEnd = async () => {
    if (!confirmSession) return;
    try {
      await forceEnd.mutateAsync(confirmSession.sessionId);
      setConfirmSession(null);
    } catch (err: any) {
      const msg: string = err?.response?.data?.error || err?.message || 'Unknown error';
      // Overwrite mutation error message with the server's message
      if (err && !err.message?.includes(msg)) err.message = msg;
      // If already completed, refresh so it disappears from the list
      if (msg.toLowerCase().includes('already')) refetch();
    }
  };

  const groupEntries = data
    ? Object.entries(data.groups).sort(([a], [b]) => {
        if (a === '__none__') return 1;
        if (b === '__none__') return -1;
        return a.localeCompare(b);
      })
    : [];

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Activity size={22} className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Active Sessions</h1>
              <p className="text-sm text-slate-500 font-medium">
                {data ? `${data.totalActive} active session${data.totalActive !== 1 ? 's' : ''} · auto-refreshes every 30s` : 'Live view of all running sessions'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!breakGlassCtx && (
              <button
                onClick={() => setBreakGlassModalOpen(true)}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
              >
                <ShieldAlert size={16} className="text-amber-500" />
                Break Glass
              </button>
            )}
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
            >
              <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
            </button>
          </div>
        </header>

        {/* Break glass active banner */}
        {breakGlassCtx && (
          <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800 text-sm">
              <ShieldAlert size={16} className="text-amber-500 shrink-0" />
              <span>Break glass active — showing project and session names</span>
            </div>
            <button
              onClick={() => setBreakGlassCtx(null)}
              className="flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:text-amber-900 transition-colors"
            >
              <X size={14} /> Dismiss
            </button>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20 text-slate-400">
            <RefreshCw size={20} className="animate-spin mr-2" /> Loading sessions…
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-sm text-red-700">
            Failed to load active sessions.
          </div>
        ) : groupEntries.length === 0 ? (
          <div className="rounded-xl bg-white p-12 shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200 text-center">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Activity size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No active sessions.</p>
            <p className="text-slate-400 text-xs mt-2">All sessions are currently idle or completed.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {groupEntries.map(([projectId, sessions]) => {
              const isUnlinked = projectId === '__none__';
              const projectName = sessions[0]?.projectName;
              const nameRedacted = sessions[0]?.nameRedacted;

              const groupLabel = isUnlinked
                ? 'No Project'
                : projectName || projectId;

              const groupSublabel = isUnlinked
                ? 'Sessions not linked to a project'
                : nameRedacted
                  ? `Project ID: ${projectId} · use break glass to reveal name`
                  : `Project ID: ${projectId}`;

              return (
                <div key={projectId} className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
                  {/* Project header */}
                  <div className="px-5 py-3 bg-slate-50/60 border-b border-[#e2e8f0] flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-navy">{groupLabel}</span>
                      <span className="ml-3 text-xs text-slate-400">{groupSublabel}</span>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                      {sessions.length} session{sessions.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Sessions table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                          <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Session</th>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Phase</th>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Last Activity</th>
                          <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                          <th className="px-5 py-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#e2e8f0]">
                        {sessions.map((session) => (
                          <tr key={session.sessionId} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-5 py-3">
                              {session.nameRedacted ? (
                                <span className="font-mono text-xs text-slate-500">{session.sessionId}</span>
                              ) : (
                                <div>
                                  <p className="font-medium text-navy">{session.customerName}</p>
                                  <p className="text-xs text-slate-400 font-mono mt-0.5">{session.sessionId}</p>
                                </div>
                              )}
                            </td>
                            <td className="px-5 py-3 text-slate-600">{formatPhase(session.phase)}</td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-1.5 text-slate-600">
                                <Clock size={13} className="text-slate-400 shrink-0" />
                                {formatRelative(session.lastActivityAt)}
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              {session.stale ? (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                  <AlertTriangle size={11} /> Stale (&gt;{STALE_DAYS}d)
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
                                  Active
                                </span>
                              )}
                            </td>
                            <td className="px-5 py-3 text-right">
                              <button
                                onClick={() => setConfirmSession(session)}
                                className="flex items-center gap-1.5 rounded-lg h-7 px-3 border border-red-200 bg-red-50 text-red-600 text-xs font-semibold hover:bg-red-100 transition-colors"
                              >
                                <Power size={12} /> End
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      <BreakGlassModal
        isOpen={breakGlassModalOpen}
        onClose={() => setBreakGlassModalOpen(false)}
        onConfirm={(ctx) => { setBreakGlassCtx(ctx); setBreakGlassModalOpen(false); }}
        action="view session and project names"
      />

      {confirmSession && (
        <EndConfirmDialog
          session={confirmSession}
          onCancel={() => { setConfirmSession(null); forceEnd.reset(); }}
          onConfirm={handleForceEnd}
          loading={forceEnd.isPending}
          error={forceEnd.error}
        />
      )}
    </div>
  );
};

export default ActiveSessions;
